// CameraRayInside
// Features:
// - Raycasts from camera to detect interactive objects inside the cabin
// - Updates global store with currently hovered object name
// - Filters out player and bounds collisions

import { useRef } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import { useRapier } from '@react-three/rapier'
import * as THREE from 'three'

import { useStore } from '../store/useStore'

const DEFAULT_MAX_DISTANCE = 2.3
const IGNORED_NAMES = ['player', 'room_bounds', 'cabin_outside']

export default function CameraRayInside({
    maxDistance = DEFAULT_MAX_DISTANCE,
    solid = true,
    onHit,
    ignorePlayer = true,
}) {
    const { camera } = useThree()
    const { rapier, world } = useRapier()
    const lastNameRef = useRef(null)

    // Reusable vector for ray direction to avoid GC
    const rayDir = useRef(new THREE.Vector3())

    useFrame(() => {
        if (!camera || !world) return

        // 1. Setup Ray
        camera.getWorldDirection(rayDir.current).normalize()

        const ray = new rapier.Ray(camera.position, rayDir.current)

        // 2. Raycast
        const hit = world.castRay(
            ray,
            maxDistance,
            solid,
            undefined, // groups
            undefined, // filter
            undefined, // filter
            undefined, // collider
            // Custom filter predicate
            ignorePlayer
                ? (collider) => {
                      const parent = collider.parent && collider.parent()
                      const name = parent?.userData?.name
                      return !IGNORED_NAMES.includes(name)
                  }
                : undefined
        )

        // 3. Handle Custom onHit Callback (Optional)
        if (onHit) {
            if (hit) {
                const point = new THREE.Vector3()
                    .copy(rayDir.current)
                    .multiplyScalar(hit.toi)
                    .add(camera.position)

                onHit({ hit, toi: hit.toi, collider: hit.collider, point })
            } else {
                onHit(null)
            }
        }

        // 4. Update Store with Hit Name
        let hitName = null
        if (hit?.collider) {
            const parent = hit.collider.parent && hit.collider.parent()
            hitName = parent?.userData?.name
        }

        if (hitName !== lastNameRef.current) {
            lastNameRef.current = hitName || null
            useStore.getState().setCurrentHitName(hitName || null)
        }
    })

    return null
}
